import { IExamQuestionAdminMetaVm } from '../../shared/models/exam-question-admin-meta-vm';

/**
 * The translation rules of the API (QuestionTranslations, QuestionMachineTranslator) on the panel side, so a list
 * edited in place (details, a DeepL run) shows the same state as after a reload.
 */
type QuestionTexts = Pick<
  IExamQuestionAdminMetaVm,
  | 'question' | 'answerA' | 'answerB' | 'answerC'
  | 'questionUA' | 'answerUAA' | 'answerUAB' | 'answerUAC'
  | 'questionDE' | 'answerDEA' | 'answerDEB' | 'answerDEC'
  | 'questionENG' | 'answerENGA' | 'answerENGB' | 'answerENGC'
>;

/** Which part of the "Bez tłumaczenia ministerstwa" tab is on screen. */
export type MinistryTranslationView = 'missing' | 'own' | 'all';

function isBlank(value: string | null | undefined): boolean {
  return !value || !value.trim();
}

/** A three-answer question needs its answers translated too; a yes/no question keeps answer A empty. */
function hasAnswers(q: Pick<IExamQuestionAdminMetaVm, 'answerA'>): boolean {
  return !isBlank(q.answerA);
}

function lacks(q: QuestionTexts, question: string | null, a: string, b: string, c: string): boolean {
  if (isBlank(question)) {
    return true;
  }
  return hasAnswers(q) && (isBlank(a) || isBlank(b) || isBlank(c));
}

/** Language codes ("UA", "DE", "EN") the question has no complete text in - missingTranslations of the API. */
export function missingTranslations(q: QuestionTexts): string[] {
  const missing: string[] = [];
  if (lacks(q, q.questionUA, q.answerUAA, q.answerUAB, q.answerUAC)) missing.push('UA');
  if (lacks(q, q.questionDE, q.answerDEA, q.answerDEB, q.answerDEC)) missing.push('DE');
  if (lacks(q, q.questionENG, q.answerENGA, q.answerENGB, q.answerENGC)) missing.push('EN');
  return missing;
}

/** No complete Ukrainian text: the API's missingTranslations when the row has it, else worked out from the texts. */
export function lacksUa(q: QuestionTexts & Pick<IExamQuestionAdminMetaVm, 'missingTranslations'>): boolean {
  if (Array.isArray(q.missingTranslations)) {
    return q.missingTranslations.includes('UA');
  }
  return lacks(q, q.questionUA, q.answerUAA, q.answerUAB, q.answerUAC);
}

/**
 * An active question whose Ukrainian text the ministry does not ship: still untranslated, or translated by us
 * (DeepL or an editor - flagged, so the student sees the "unofficial translation" notice).
 */
export function isWithoutMinistryUa(q: IExamQuestionAdminMetaVm): boolean {
  return q.isDeleted !== true && (q.uaMachineTranslated === true || lacksUa(q));
}

export function matchesMinistryView(q: IExamQuestionAdminMetaVm, view: MinistryTranslationView): boolean {
  switch (view) {
    case 'missing':
      return lacksUa(q);
    case 'own':
      return !lacksUa(q);
    default:
      return true;
  }
}

/**
 * The Polish texts DeepL would get for this question: every empty Ukrainian field whose Polish source is not
 * empty (QuestionMachineTranslator.MissingTexts). Their length is what the DeepL plan counts.
 */
export function textsToTranslate(q: QuestionTexts): string[] {
  const texts: string[] = [];
  const add = (source: string | null, current: string | null) => {
    if (!isBlank(source) && isBlank(current)) {
      texts.push(source!.trim());
    }
  };
  add(q.question, q.questionUA);
  if (hasAnswers(q)) {
    add(q.answerA, q.answerUAA);
    add(q.answerB, q.answerUAB);
    add(q.answerC, q.answerUAC);
  }
  return texts;
}
